import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.65, 0, 0.35, 1];

export const MaskedLines = ({ lines, className = "", lineClassName = "", delay = 0, as = "h2", inView = false }) => {
    const Tag = as;
    const anim = inView
        ? { whileInView: { y: "0%" }, viewport: { once: true, margin: "-10% 0px" } }
        : { animate: { y: "0%" } };
    return (
        <Tag className={className}>
            {lines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
                    <motion.span
                        className={`block ${lineClassName}`}
                        initial={{ y: "115%" }}
                        {...anim}
                        transition={{ duration: 1, ease: EASE, delay: delay + i * 0.12 }}
                    >
                        {line}
                    </motion.span>
                </span>
            ))}
        </Tag>
    );
};

export const FadeUp = ({ children, className = "", delay = 0, y = 32 }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-8% 0px" }}
        transition={{ duration: 0.9, ease: EASE, delay }}
    >
        {children}
    </motion.div>
);

export const ParallaxImage = ({ src, alt, className = "", imgClassName = "", speed = 10, eager = false }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [`-${speed}%`, `${speed}%`]);
    return (
        <div ref={ref} className={`relative overflow-hidden ${className}`}>
            <motion.img
                src={src}
                alt={alt}
                loading={eager ? "eager" : "lazy"}
                style={{ y }}
                className={`absolute inset-0 w-full h-full object-cover scale-[1.22] ${imgClassName}`}
            />
        </div>
    );
};

export const Eyebrow = ({ children, dark = false, className = "" }) => (
    <p className={`flex items-center gap-3 font-sans text-[11px] font-medium tracking-[0.28em] uppercase ${dark ? "text-fog" : "text-slate"} ${className}`}>
        <span className="inline-block w-8 h-px bg-gold" aria-hidden="true" />
        {children}
    </p>
);

export const ArrowLink = ({ children, to, dark = false, testid, className = "" }) => (
    <a
        href={to}
        data-testid={testid}
        className={`group inline-flex items-center gap-3 font-sans text-xs font-medium tracking-[0.22em] uppercase link-rule ${dark ? "text-chalk" : "text-graphite"} ${className}`}
    >
        {children}
        <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true">→</span>
    </a>
);

export const CtaButton = ({ children, to, testid, variant = "solid", className = "" }) => {
    const base = "group relative inline-flex items-center justify-center gap-3 px-8 py-4 font-sans text-xs font-semibold tracking-[0.22em] uppercase overflow-hidden transition-colors duration-500";
    const styles = variant === "solid"
        ? "bg-forest text-chalk hover:bg-graphite"
        : "border border-chalk/40 text-chalk hover:border-gold hover:text-gold";
    return (
        <a href={to} data-testid={testid} className={`${base} ${styles} ${className}`}>
            {children}
            <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true">→</span>
        </a>
    );
};
